import { forwardRef } from 'react';
import type {
  InputBaseProps as MantineInputBaseProps,
  InputPlaceholderProps as MantineInputPlaceholderProps,
} from '@mantine/core';
import {
  createPolymorphicComponent,
  Input as MantineInput,
  InputBase as MantineInputBase,
} from '@mantine/core';

import type { LockedPolymorphic, LockedProps } from '../types';

export type InputBaseProps = Omit<MantineInputBaseProps, LockedProps>;

type InputPlaceholderProps = Omit<MantineInputPlaceholderProps, LockedProps>;

const _InputBase = forwardRef<HTMLInputElement, InputBaseProps>((props, ref) => {
  return <MantineInputBase {...props} ref={ref} />;
});

_InputBase.displayName = 'InputBase';

// InputBase is Mantine's base for custom pickers, and that pattern renders it as a button
// (`component="button"` with a Placeholder child), so the lock needs the re-narrowing cast that
// keeps `component=` working — the same shape as Menu.Item.
const _PolymorphicInputBase = createPolymorphicComponent<'input', InputBaseProps>(
  _InputBase,
) as LockedPolymorphic<'input', InputBaseProps>;

const InputPlaceholder = forwardRef<HTMLSpanElement, InputPlaceholderProps>((props, ref) => {
  return <MantineInput.Placeholder {...props} ref={ref} />;
});

InputPlaceholder.displayName = 'InputBase.Placeholder';

/**
 * ### InputBase
 *
 * @description
 * Mantine's InputBase minus per-call-site style overrides (see {@link LockedProps}): the shell a
 * custom picker renders as its trigger — `component="button"` plus `InputBase.Placeholder` until a
 * value exists — with the label/error/section plumbing of every other input.
 *
 * @example
 * ```tsx
 * <InputBase component="button" type="button" label="Category" pointer>
 *   {selected ?? <InputBase.Placeholder>Select</InputBase.Placeholder>}
 * </InputBase>
 * ```
 *
 * @param props - {@link InputBaseProps}
 * @returns React component
 */
export const InputBase = Object.assign(_PolymorphicInputBase, {
  Placeholder: InputPlaceholder,
});
